import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import api from '../../services/api';

dayjs.locale('fr');

const EphemerisPreview = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const [ephemeris, setEphemeris] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000/api';

  const fetchEphemeris = useCallback(async (targetDate) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/ephemeris/date/${targetDate}`);
      setEphemeris(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (date) {
      fetchEphemeris(date);
    }
  }, [date, fetchEphemeris]);

  const getImageUrl = (type = 'frame') => {
    if (!ephemeris) return null;
    
    // Déterminer le chemin selon le type et la langue
    let imagePath;
    if (type === 'frame') {
      // Préférer la version FR si elle existe, sinon la version par défaut
      imagePath = ephemeris.image_path_with_frame_fr || ephemeris.image_path_with_frame;
    } else {
      imagePath = ephemeris.image_path_simple_fr || ephemeris.image_path_simple;
    }
    
    if (!imagePath) return null;
    
    // Nettoyer le chemin si nécessaire
    const cleanPath = imagePath.replace(/^\/app\/generated\//, '');
    
    return `${API_URL.replace('/api', '')}/generated/${cleanPath}`;
  };

  const handlePrevDay = () => {
    const prevDate = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');
    navigate(`/ephemerides/preview/${prevDate}`);
  };

  const handleNextDay = () => {
    const nextDate = dayjs(date).add(1, 'day').format('YYYY-MM-DD');
    navigate(`/ephemerides/preview/${nextDate}`);
  };

  const handleSend = async () => {
    if (!ephemeris) return;
    try {
      await api.post('/whatsapp/send-daily', { ephemeride_id: ephemeris.id });
      setSuccess('Envoi lancé avec succès');
    } catch (err) {
      setError('Erreur lors de l\'envoi');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/ephemerides')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Aperçu de l'éphéméride
        </Typography>
        <IconButton onClick={handlePrevDay} title="Jour précédent">
          <PrevIcon />
        </IconButton>
        <Chip 
          label={dayjs(date).format('dddd D MMMM YYYY')} 
          color="primary" 
          sx={{ textTransform: 'capitalize' }}
        />
        <IconButton onClick={handleNextDay} title="Jour suivant">
          <NextIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {ephemeris ? (
        <Grid container spacing={3}>
          {/* Images */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Version avec cadre
                  <Chip label="WhatsApp" size="small" color="success" sx={{ ml: 1 }} />
                </Typography>
              </CardContent>
              {ephemeris.image_path_with_frame ? (
                <CardMedia
                  component="img"
                  image={getImageUrl('frame')}
                  alt="Éphéméride avec cadre"
                  sx={{ maxHeight: 600, objectFit: 'contain', bgcolor: 'grey.100' }}
                />
              ) : (
                <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ImageIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                    <Typography color="textSecondary">Image non générée</Typography>
                  </Box>
                </Box>
              )}
              <CardActions>
                <Button
                  startIcon={<DownloadIcon />}
                  href={getImageUrl('frame')}
                  target="_blank"
                  disabled={!ephemeris.image_path_with_frame}
                >
                  Télécharger
                </Button>
                <Button
                  startIcon={<SendIcon />}
                  color="success"
                  onClick={handleSend}
                  disabled={!ephemeris.image_path_with_frame}
                >
                  Envoyer
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Version simple
                  <Chip label="Impression" size="small" color="secondary" sx={{ ml: 1 }} />
                </Typography>
              </CardContent>
              {ephemeris.image_path_simple ? (
                <CardMedia
                  component="img"
                  image={getImageUrl('simple')}
                  alt="Éphéméride simple"
                  sx={{ maxHeight: 600, objectFit: 'contain', bgcolor: 'grey.100' }}
                />
              ) : (
                <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ImageIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                    <Typography color="textSecondary">Image non générée</Typography>
                  </Box>
                </Box>
              )}
              <CardActions>
                <Button
                  startIcon={<DownloadIcon />}
                  href={getImageUrl('simple')}
                  target="_blank"
                  disabled={!ephemeris.image_path_simple}
                >
                  Télécharger
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Informations détaillées */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Informations</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={3}>
                  {/* Date */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">Date</Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {dayjs(ephemeris.date).format('dddd D MMMM YYYY')}
                    </Typography>
                  </Grid>

                  {/* Jour en Duala */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">Jour (Duala)</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {ephemeris.weekday?.duala_name || '-'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {ephemeris.weekday?.french_name}
                    </Typography>
                  </Grid>

                  {/* Mois en Duala */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">Mois (Duala)</Typography>
                    <Typography variant="h6" fontWeight="bold" color="secondary">
                      {ephemeris.month?.duala_name || '-'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {ephemeris.month?.french_name}
                    </Typography>
                  </Grid>

                  {/* Statut */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">Statut</Typography>
                    <Chip
                      label={ephemeris.is_generated ? 'Générée' : 'Non générée'}
                      color={ephemeris.is_generated ? 'success' : 'warning'}
                    />
                    {ephemeris.generated_at && (
                      <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                        Générée le {dayjs(ephemeris.generated_at).format('DD/MM/YYYY à HH:mm')}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Proverbe */}
                {ephemeris.proverb && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>Proverbe (MUNIA)</Typography>
                    
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body1" fontWeight="bold" gutterBottom>
                        {ephemeris.proverb.duala_text}
                      </Typography>
                      
                      <Typography variant="body1" color="error.main" fontStyle="italic" sx={{ mt: 2 }}>
                        {ephemeris.proverb.french_text}
                      </Typography>
                      
                      {ephemeris.proverb.french_meaning && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                          <strong>Sens :</strong> {ephemeris.proverb.french_meaning}
                        </Typography>
                      )}
                    </Paper>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary" gutterBottom>
            Aucune éphéméride trouvée pour cette date
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => fetchEphemeris(date)}
          >
            Générer l'éphéméride
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default EphemerisPreview;
